<template>
    <div class="population-selector-component">
        <div class="preset-column">
            <input-container
                v-model="populations"
                :showTooltip="true"
            ></input-container>
            <div class="label" :style="{marginLeft: 10 + 'px'}">Population size <span class="field-info" v-tooltip="'Amount of subjects to be generated'"></span></div>
            <sub-field-list-input-container class="size-box"
                :fieldConfig="fieldConfig[2]"
                :showLabel="false"
                :showTooltip="true"
            ></sub-field-list-input-container>
            <input-container id="disabled"
                v-model="fieldConfig[3]"
                :showTooltip="true"
            ></input-container>
        </div>
        <div class="basis-column">
            <div class="label">Population Information <span class="field-info" v-tooltip="'Population description and parameters'"></span></div>
            <div class="supplementary-info">This population was derived from a <b>limited number of anatomies</b>, results can provide valuable trend information and insights but they will not fully represent the diversity of real-world populations.</div>
            <div class="info-box">
                <div class="descriptors">
                    <div class="item" v-for="descriptor in populationDescriptors"> {{ descriptor }}</div>
                </div>
                <div class="infos">
                    <div class="item" v-for="info,i in populationsInfo[selectedPopulation]">{{ info }}</div>
                </div>
            </div>
        </div>
        <div class="plot-column">
            <div class="supplementary-info">The following plot shows the <b>body mass index (BMI) distribution</b> among men and women aged 18 years and over using data from the Health Survey for England between 2016 and 2018. This data was standardised to the English population at the time of measurement and therefore represent the BMI distribution within the adult population at that time. The curves displayed are approximations to provide a clearer visual representation of BMI trends. Actual BMI frequencies may vary. <b>The points represent where the average of the selected population stands in the distribution.</b></div>
            <bmi-plot class="bmi-plot" :selectedPopulation="selectedPopulation[0]" :trialConfigType="'single'"></bmi-plot>
        </div>
    </div>
</template>

<script>
import MultiSelectComponent from '@/components/MultiSelectComponent'
import ToggleInput from '@/components/inputs/frontendInputs/ToggleInput'
import InputContainer from '@/components/inputs/InputContainer'
import SubFieldListInputContainer from '@/components/inputs/SubFieldListInputContainer'
import BmiPlot from '@/components/trial/BmiPlot.vue'


export default {
    name: 'PopulationSelectorComponent',
    components: { MultiSelectComponent, ToggleInput, InputContainer, SubFieldListInputContainer, BmiPlot },
    props: {
        fieldConfig: Array
    },
    model: {
        prop: 'fieldConfig',
        event: 'change'
    },
    data() {
        return {
            error: '',
            populationDescriptors : ['Female ratio', 'Average Age', 'Average BSA', 'Average Male BMI', 'Average Female BMI', 'BMI classification', 'Scar', 'Average Volume'],
            populationsInfo: {
                'Underweight': ['0.5', '35', '1.41 m2', '19.1 kg/m2', '19.1 kg/m2', 'Underweight', 'No', '189 cm3'],
                'Healthy BMI': ['0.5',  '28', '1.89 m2', '31.2 kg/m2', '31.2 kg/m2', 'Healthy', 'No', '214 cm3'],
                'Healthy BMI Scarred': ['0.5', '28', '1.89 m2', '31.2 kg/m2', '31.2 kg/m2', 'Healthy', 'Yes', '214 cm3'],
                'Pediatric': ['0.5', '5', '-', '19.8 kg/m2', '16.18 kg/m2', 'Underweight', 'No', '176.5 cm3'],
                'Healthy Adults': ['0.5', '43', '-', '27.17 kg/m2', '28.3 kg/m2', 'Overweight', 'No', '310.6 cm3'],
                'Dilated Cardiomyopathy': ['0.5', '59', '-', '25.31 kg/m2', '19.82 kg/m2', 'Healthy', 'No', '452.8 cm3' ],
                'Hypertrophic Cardiomyopathy': ['0.5', '81', '-', '30.2 kg/m2', '25.8 kg/m2', 'Overweight', 'No', '301.59 cm3'],
                'Heart Failure': ['0.5', '60', '-', '21.8 kg/m2', '22 kg/m2', 'Healthy', 'No', '261.3 cm3'],
                'Ischemia': ['0.5', '60', '-', '21.8 kg/m2', '22 kg/m2', 'Healthy', 'No', '261.3 cm3'],
                'Myocardial Infarction': ['0.5', '60', '-', '21.8 kg/m2', '22 kg/m2', 'Healthy', 'No', '261.3 cm3'],
            }
        }
    },
    computed: {
        populations() {
            return this.fieldConfig[1]
        },
        selectedPopulation() {
            return this.fieldConfig[1].value
        },
    }
}
</script>

<style lang="stylus">
.population-selector-component
    display: flex
    flex-direction: row
    justify-content: space-between
    #disabled
        opacity: 0.4
        pointer-events: none
    .label
        color: $shuttle-gray
        display: flex
        align-items: center
        m-font-size(12, 14)
        margin-bottom: 5px
        .field-info
            margin-left: 5px
            m-icon('info', 14)
    .supplementary-info
        m-font-size(12,14)
        color: $cloud-burst
        text-align: justify

    .preset-column
        width: 20%
        flex-grow: 0
        .size-box
            margin-top: 10px
            margin-bottom: 22px
            
    .basis-column
        width: 30%
        padding-top: 5px
        .info-box
            margin-top: 10px
            background-color: white
            padding: 10px
            margin-bottom: 20px
            m-font-size(12,14)
            color: $cloud-burst
            border-radius: 5px
            box-shadow: 5px 5px 20px rgba($lynch, 0.25)
            display: flex
            flex-direction: row
            .descriptors
                font-weight: bold
                margin-right: 30px
            .item
                padding-bottom: 5px

    .plot-column
        width: 48%
        padding: 10px
        height: 21vw
        .bmi-plot
            height: 21vw
            margin-top: 10px
            border-radius: 5px
            box-shadow: 5px 5px 20px rgba($lynch, 0.25)
</style>